import React, { useState } from 'react';
import { Mail, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable newsletter sign-up CTA.
 * variant: 'dark' (navy bg, for dark pages) | 'light' (ivory bg, for light pages)
 */
const NewsletterCTA = ({ variant = 'dark', source = 'unknown', className = '' }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message || 'Check your inbox to confirm your subscription.');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  const isDark = variant === 'dark';

  if (status === 'success') {
    return (
      <div
        className={`rounded-2xl p-8 text-center ${isDark ? 'bg-white/[0.03] border border-gold/[0.08]' : 'bg-[#071B3A] border border-[#C99316]/20'} ${className}`}
        data-testid="newsletter-cta-success"
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 mb-4">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
        </div>
        <p className="font-serif text-lg text-cream font-light mb-2">
          {isDark ? <span className="text-[#FAF8F3]">Confirm your email</span> : <span className="text-[#FAF8F3]">Confirm your email</span>}
        </p>
        <p className={`text-sm leading-relaxed ${isDark ? 'text-[#FAF8F3]/40' : 'text-[#FAF8F3]/50'}`}>{message}</p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-white/[0.03] border border-gold/[0.08]' : 'bg-[#071B3A]'} ${className}`}
      data-testid="newsletter-cta"
    >
      <div className="p-8 sm:p-10">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-gold/10' : 'bg-[#C99316]/10'}`}>
            <Mail className="w-5 h-5 text-[#C99316]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#C99316]/60 font-sans mb-1">Cultural Fluency List</p>
            <h3 className="font-serif text-xl text-[#FAF8F3] font-light leading-snug">
              Stay close to the work
            </h3>
            <p className={`text-sm leading-relaxed mt-1 ${isDark ? 'text-[#FAF8F3]/35' : 'text-[#FAF8F3]/40'}`}>
              New essays, frameworks and session insights — delivered when they're ready, not on a schedule.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              type="text"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="First name"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30 transition-colors"
              data-testid="newsletter-firstname-input"
            />
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30 transition-colors"
              data-testid="newsletter-email-input"
            />
          </div>

          {status === 'error' && (
            <p className="text-red-400 text-xs">{message}</p>
          )}

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="submit"
              disabled={!email || status === 'loading'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#C99316] text-[#071B3A] text-sm font-semibold rounded-xl hover:bg-[#C99316]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all touch-manipulation"
              data-testid="newsletter-submit-btn"
            >
              {status === 'loading'
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Subscribing...</>
                : <>Join the list <ArrowRight className="w-4 h-4" /></>
              }
            </button>
            <p className="text-[#FAF8F3]/20 text-xs">
              Double opt-in. No spam. Unsubscribe any time.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsletterCTA;
